import React, { Suspense, useEffect, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import CookieBanner from './components/CookieBanner';
import { getBrand } from './config/brands';
import { consultaMedicaOnline, receitaDigital, atestadoMedicoOnline, medicoOnlineAgora } from './config/seoPages';
import './index.css';

// Eager: landing pages (critical path)
import LandingPage from './pages/LandingPage';
import UrologiaLanding from './pages/UrologiaLanding';

// Lazy: everything else (loaded on demand)
const PatientLogin = lazy(() => import('./pages/PatientLogin'));
const DoctorLogin = lazy(() => import('./pages/DoctorLogin'));
const PatientRegistration = lazy(() => import('./pages/PatientRegistration'));
const PatientHome = lazy(() => import('./pages/PatientHome'));
const PatientProfile = lazy(() => import('./pages/PatientProfile'));
const PatientChat = lazy(() => import('./pages/PatientChat'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const DoctorDashboard = lazy(() => import('./pages/DoctorDashboard'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const PatientHistory = lazy(() => import('./pages/PatientHistory'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const BlogList = lazy(() => import('./pages/BlogList'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const SEOLandingPage = lazy(() => import('./pages/SEOLandingPage'));
const AuthorPage = lazy(() => import('./pages/AuthorPage'));

// Lazy: payment pages
const PaymentPages = lazy(() => import('./pages/PaymentPages'));
const PaymentSuccess = lazy(() => import('./pages/PaymentPages').then(m => ({ default: m.PaymentSuccess })));
const PaymentCancel = lazy(() => import('./pages/PaymentPages').then(m => ({ default: m.PaymentCancel })));

// Loading fallback
const PageLoader = () => (
  <div className="h-screen flex items-center justify-center bg-gray-50">
    <div className="spinner"></div>
  </div>
);

// Redirect legacy ?post=slug URLs to /blog/slug (fixes Google "duplicate without canonical")
function LegacyPostRedirect() {
  const [searchParams] = useSearchParams();
  const postSlug = searchParams.get('post');
  if (postSlug) {
    return <Navigate to={`/blog/${postSlug}`} replace />;
  }
  // Brand detection: show different landing per domain
  const brand = getBrand();
  if (brand.id === 'urologista') {
    return <UrologiaLanding />;
  }
  return <LandingPage />;
}

// Redirect www to non-www (client-side fallback for when server redirect isn't configured)
function WwwRedirect({ children }) {
  useEffect(() => {
    if (window.location.hostname.startsWith('www.')) {
      window.location.replace(
        window.location.href.replace('://www.', '://')
      );
    }
  }, []);
  return children;
}

function PrivateRoute({ children, requireDoctor = false, requireAdmin = false, requirePatient = false }) {
  const { user, loading, token, adminToken } = useAuth();
  
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-100">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (requireAdmin) {
    if (!adminToken) return <Navigate to="/medico?tab=admin" replace />;
    return children;
  }
  
  if (!token || !user) return <Navigate to="/medico" replace />;
  
  if (requireDoctor && user.role !== 'doctor') return <Navigate to="/paciente" replace />;
  if (requirePatient && user.role !== 'patient') return <Navigate to="/dashboard" replace />;
  
  return children;
}

function AppRoutes() {
  const { user, token, adminToken } = useAuth();
  
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<LegacyPostRedirect />} />
        <Route path="/login" element={
          token && user ? (
            <Navigate to={user.role === 'doctor' ? '/dashboard' : '/paciente'} replace />
          ) : <PatientLogin />
        } />
        <Route path="/medico" element={
          token && user?.role === 'doctor' ? (
            <Navigate to="/dashboard" replace />
          ) : adminToken ? (
            <Navigate to="/admin" replace />
          ) : <DoctorLogin />
        } />
        <Route path="/cadastro" element={token ? <Navigate to="/paciente" replace /> : <PatientRegistration />} />
        <Route path="/recuperar-senha" element={token ? <Navigate to="/paciente" replace /> : <ForgotPassword />} />
        <Route path="/historico/:patientId" element={<PatientHistory />} />
        <Route path="/termos" element={<TermsPage />} />
        <Route path="/privacidade" element={<TermsPage />} />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/autor/:authorSlug" element={<AuthorPage />} />
        <Route path="/consulta-medica-online" element={<SEOLandingPage config={consultaMedicaOnline} />} />
        <Route path="/receita-digital" element={<SEOLandingPage config={receitaDigital} />} />
        <Route path="/atestado-medico-online" element={<SEOLandingPage config={atestadoMedicoOnline} />} />
        <Route path="/medico-online-agora" element={<SEOLandingPage config={medicoOnlineAgora} />} />
        <Route path="/pagamento-sucesso" element={<PaymentSuccess />} />
        <Route path="/pagamento-cancelado" element={<PaymentCancel />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/payment-cancel" element={<PaymentCancel />} />
        <Route path="/paciente" element={<PrivateRoute requirePatient><PatientHome /></PrivateRoute>} />
        <Route path="/perfil" element={<PrivateRoute requirePatient><PatientProfile /></PrivateRoute>} />
        <Route path="/consulta/:consultationId" element={<PrivateRoute requirePatient><PatientChat /></PrivateRoute>} />
        <Route path="/dashboard" element={<PrivateRoute requireDoctor><DoctorDashboard /></PrivateRoute>} />
        <Route path="/admin" element={<PrivateRoute requireAdmin><AdminPanel /></PrivateRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function AppRouter() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id BEFORE rendering routes (prevents race conditions)
  if (location.hash?.includes('session_id=')) {
    return <Suspense fallback={<PageLoader />}><AuthCallback /></Suspense>;
  }
  return <AppRoutes />;
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <WwwRedirect>
            <AppRouter />
            <CookieBanner />
          </WwwRedirect>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
